import type { VerificationChallenge as Challenge, VerificationMethods } from '@tenfour/contracts';
import { VerificationChallenge, type VerificationResult } from '@/components/auth/VerificationChallenge';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ForgotPasswordAction } from '@/components/auth/ForgotPasswordAction';
import { useAuth, type AuthUser } from '@/components/auth/AuthContext';
import {
  AuthShell,
  FieldError,
  RequiredMark,
  authFieldProps,
  focusFirstInvalid,
} from '@/components/auth/AuthShell';
import { BrandLockup } from '@/components/BrandLockup';
import { SsoButtons } from '@/components/auth/SsoButtons';
import { AccountSecuritySetup } from '@/components/auth/AccountSecuritySetup';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { adminLogin, adminPasskeyLogin, acceptAdminVerification } from '@/modules/admin/api';
import { api } from '@/lib/api';
import { getSecuritySetupOffer } from '@/lib/securitySetup';
import styles from './login.module.css';

// New accounts self-serve through the public outreach signup flow — the login
// footer links /outreach/signup (supersedes the barred-signup era of #39).
type Mode = 'login' | 'forgot';
type Pane = 'credentials' | 'security';

const FADE_MS = 320;

function readSessionUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem('tt_user');
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

/**
 * The sign-in page, mounted twice: `/login` for contractors and `/admin/login`
 * with `admin` set. The two are deliberately the same screen — the only visible
 * difference is the AuthShell's ADMIN badge — so `admin` may only steer which
 * credential call runs and where a success lands. Don't fork the page.
 *
 * After a contractor password sign-in, eligible users see an inline
 * account-security setup step (credentials fade out → prompt fades in) before the
 * portal redirect. Admin + passkey sign-in skip that step.
 */
export default function LoginPage({ admin = false }: { admin?: boolean }) {
  const { signIn, signInWithPasskey, establishSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  useEffect(() => {
    document.title = admin ? 'Admin Sign In | Ten Four' : 'Sign In | Ten Four';
  }, [admin]);
  // Surface SSO failures: the callback bounces back to /login?sso_error=… when
  // the provider login can't be linked to a Ten Four account.
  useEffect(() => {
    const ssoError = searchParams.get('sso_error');
    if (ssoError) setError(ssoError);
  }, [searchParams]);
  const [verification, setVerification] = useState<Challenge | null>(null);
  useEffect(() => {
    const raw = new URLSearchParams(window.location.hash.slice(1)).get('verification');
    if (!raw) return;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    try {
      const challenge = JSON.parse(raw) as Challenge;
      if (challenge.twoFactorRequired && /^[a-f0-9]{64}$/.test(challenge.challengeToken) && Array.isArray(challenge.methods)) setVerification(challenge);
    } catch { setError('Please sign in again.'); }
  }, []);
  const mode: Mode = searchParams.get('mode') === 'forgot' ? 'forgot' : 'login';
  const [pane, setPane] = useState<Pane>('credentials');
  const [paneVisible, setPaneVisible] = useState(true);
  const [email, setEmail] = useState('');
  useEffect(() => {
    const resetEmail = (location.state as { resetEmail?: string } | null)?.resetEmail;
    if (typeof resetEmail === 'string') setEmail(resetEmail);
  }, [location.state]);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  // The same required-marker + inline-error contract signup carries.
  // Keyed by control id so the message, the aria wiring and the focus target
  // are all one lookup.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [securityMethods, setSecurityMethods] = useState<VerificationMethods | null>(null);
  const fadeTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (fadeTimer.current != null) window.clearTimeout(fadeTimer.current);
  }, []);

  const resetForm = () => {
    setError('');
    setSuccess('');
    setFieldErrors({});
    setPassword('');
  };

  const switchMode = (m: Mode) => {
    resetForm();
    const next = new URLSearchParams(searchParams);
    if (m === 'forgot') next.set('mode', 'forgot');
    else next.delete('mode');
    setSearchParams(next, { replace: true, state: location.state });
  };

  // Admins land on the Control Tower; contractors on the portal (or wherever a
  // guard bounced them from).
  const goAfterSignIn = useCallback(() => {
    if (admin) {
      navigate('/admin', { replace: true });
      return;
    }
    navigate(searchParams.get('redirect') || '/home');
  }, [admin, navigate, searchParams]);

  const fadeTo = useCallback((next: Pane, then?: () => void) => {
    setPaneVisible(false);
    if (fadeTimer.current != null) window.clearTimeout(fadeTimer.current);
    fadeTimer.current = window.setTimeout(() => {
      if (then) {
        then();
        return;
      }
      setPane(next);
      // Next paint: mount the new pane hidden, then fade it in.
      requestAnimationFrame(() => setPaneVisible(true));
    }, FADE_MS);
  }, []);

  const continueAfterContractorSignIn = useCallback(async () => {
    const user = readSessionUser();
    const offer = await getSecuritySetupOffer(user);
    if (!offer) {
      goAfterSignIn();
      return;
    }
    setPassword('');
    setSecurityMethods(offer);
    fadeTo('security');
  }, [fadeTo, goAfterSignIn]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    // The button stays enabled and the form answers per field.
    const invalid: Record<string, string> = {};
    if (!email.trim()) invalid.email = 'Enter your email';
    if (!password.trim()) invalid.password = 'Enter your password';
    if (Object.keys(invalid).length > 0) {
      setFieldErrors(invalid);
      // Source order, not object order — the first control on the page is the
      // one to focus, and `code` only exists on the 2FA step.
      focusFirstInvalid(['email', 'password'].find((id) => invalid[id]) as string);
      return;
    }
    setFieldErrors({});
    setError('');
    setLoading(true);
    try {
      const result = admin
        ? await adminLogin(email, password)
        : await signIn(email, password);
      if (result.twoFactorRequired) {
        // The password proves only the first step.
        if (!result.challenge) throw new Error('Please update the page and sign in again.');
        setVerification(result.challenge);
        setPassword('');
        setLoading(false);
        return;
      }
      if (admin) {
        goAfterSignIn();
        return;
      }
      await continueAfterContractorSignIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskey = async () => {
    setError('');
    setLoading(true);
    try {
      // Pass the email if they've typed one (scopes to that account); otherwise
      // the browser offers any discoverable passkey for this site.
      const scoped = email.trim() || undefined;
      if (admin) await adminPasskeyLogin(scoped);
      else await signInWithPasskey(scoped);
      // Already signed in with a passkey — no setup nudge.
      goAfterSignIn();
    } catch (err) {
      // A user canceling the native prompt throws NotAllowedError — don't nag.
      if (err instanceof Error && err.name === 'NotAllowedError') {
        setLoading(false);
        return;
      }
      setError(err instanceof Error ? err.message : 'Passkey sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setFieldErrors({ 'email-fp': 'Enter your email' });
      focusFirstInvalid('email-fp');
      return;
    }
    setFieldErrors({});
    setError('');
    setLoading(true);
    try {
      await api('/auth/password/request-reset', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      setSuccess('If an account exists with that email, a reset link has been sent.');
    } catch {
      setSuccess('If an account exists with that email, a reset link has been sent.');
    } finally {
      setLoading(false);
    }
  };

  const finishSecurityStep = useCallback(() => {
    fadeTo('security', goAfterSignIn);
  }, [fadeTo, goAfterSignIn]);

  // SSO returns here after storing its session, so it shares the same offer.
  const ssoSetupStarted = useRef(false);
  useEffect(() => {
    if (admin || searchParams.get('setup') !== 'security' || ssoSetupStarted.current) return;
    ssoSetupStarted.current = true;
    void continueAfterContractorSignIn();
  }, [admin, searchParams, continueAfterContractorSignIn]);

  const cancelVerification = () => { setVerification(null); setPassword(''); };
  const finishVerification = async (result: VerificationResult) => {
    const token = result.token || result.accessToken;
    if (!token || !result.user) throw new Error('Verification did not return a session.');
    if (admin) { acceptAdminVerification(result); goAfterSignIn(); }
    else { establishSession({ token, user: result.user }); await continueAfterContractorSignIn(); }
    setVerification(null);
  };

  const paneClass = [
    styles.pane,
    paneVisible ? styles.paneVisible : styles.paneHidden,
  ].join(' ');

  return (
    <AuthShell admin={admin} spacious={pane === 'security'}>
      <div className={paneClass}>
        {verification ? <VerificationChallenge initial={verification} onVerified={finishVerification} onCancel={cancelVerification} admin={admin} /> : pane === 'security' && securityMethods ? (
          <AccountSecuritySetup user={readSessionUser()} methods={securityMethods} onComplete={finishSecurityStep} />
        ) : (
          <>
            <div className={styles.welcome}>
              {mode === 'login' ? (
                <h1 className={styles.welcomeTitle}>
                  <BrandLockup className={styles.welcomeWordmark} size="1em" />
                </h1>
              ) : (
                <>
                  <h1 className={styles.welcomeTitle}>Reset your password</h1>
                  <p className={styles.welcomeSub}>We&apos;ll email you a link.</p>
                </>
              )}
            </div>

            {error && (
              <div className={styles.errorBox} role="alert">
                <AlertCircle size={18} className={styles.errorIcon} aria-hidden="true" />
                <p className={styles.errorText}>
                  {error.includes('/admin') ? (
                    <>This login is for accounts only. For admin access, navigate to <a href="/admin" className={styles.errorLink}>/admin</a> in your browser URL bar.</>
                  ) : error}
                </p>
              </div>
            )}

            {success && (
              <div className={styles.successBox} role="status" aria-live="polite">
                <CheckCircle size={18} className={styles.successIcon} aria-hidden="true" />
                <p className={styles.successText}>{success}</p>
              </div>
            )}

            {/* Passkey + Google/Microsoft/Apple SSO (#142). SsoButtons self-hides any
                provider that isn't configured; the callback routes an ADMIN to the
                admin app, so the same buttons serve both mounts. */}
            {mode === 'login' && <SsoButtons onPasskey={handlePasskey} dividerLabel="or" />}

            {mode === 'login' && (
              <form onSubmit={handleLogin} className={styles.form}>
                <div className={styles.field}>
                  <label htmlFor="email" className={styles.label}>Email<RequiredMark /></label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@business.com"
                    {...authFieldProps('email-error', fieldErrors.email, styles.input)}
                  />
                  <FieldError id="email-error" message={fieldErrors.email} />
                </div>
                {/* Password input precedes the forgot control in source order so
                    Tab from email lands on the password field; the grid keeps
                    the forgot control top-right visually. */}
                <div className={`${styles.field} ${styles.passwordField}`}>
                  <label htmlFor="password" className={`${styles.label} ${styles.passwordLabel}`}>Password<RequiredMark /></label>
                  <div className={styles.passwordWrap}>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      {...authFieldProps(
                        'password-error',
                        fieldErrors.password,
                        [styles.input, styles.passwordInput].join(' '),
                      )}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className={styles.eyeBtn} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <FieldError id="password-error" message={fieldErrors.password} />
                  <ForgotPasswordAction className={styles.forgotBtn} email={email} admin={admin} disabled={loading} />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className={styles.submitBtn}
                  aria-busy={loading}
                  aria-label={loading ? 'Signing in' : undefined}
                >
                  {loading ? <div className={styles.btnSpinner} aria-hidden="true" /> : 'Continue with email'}
                </button>
              </form>
            )}

            {/* Forgot Password Form */}
            {mode === 'forgot' && !success && (
              <form onSubmit={handleForgot} className={styles.form}>
                <div className={styles.field}>
                  <label htmlFor="email-fp" className={styles.label}>Email<RequiredMark /></label>
                  <input
                    id="email-fp"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@business.com"
                    {...authFieldProps('email-fp-error', fieldErrors['email-fp'], styles.input)}
                  />
                  <FieldError id="email-fp-error" message={fieldErrors['email-fp']} />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className={styles.submitBtn}
                  aria-busy={loading}
                  aria-label={loading ? 'Sending reset link' : undefined}
                >
                  {loading ? <div className={styles.btnSpinner} aria-hidden="true" /> : 'Send reset link'}
                </button>
              </form>
            )}

            <p className={styles.footer}>
              {mode === 'login' && (
                <Link to="/outreach/signup" className={styles.signupLink}>
                  Create your account <span className={styles.signupArrow} aria-hidden="true">→</span>
                </Link>
              )}
              {mode === 'forgot' && (
                <>Remember your password? <button className={styles.footerLink} onClick={() => switchMode('login')}>Sign in</button></>
              )}
            </p>
          </>
        )}
      </div>
    </AuthShell>
  );
}
